import React, { useEffect, useMemo, useRef, useState } from "react";
import LoadingV2 from "../../components/LoadingV2";
import PhotoIcon from "../../images/PhotoIcon";
import { webNavigate } from "../../services/navigate.service";
import { coreFormLimits, limitProps, useFieldLimits, validateAgainstLimits } from "../../services/fieldLimits";
import FieldCounter from "../../components/FieldCounter";
import {
  GRATUITY_OPTION_TYPES,
  GRATUITY_PERCENT_BASES,
  GRATUITY_PROMPT_MODES,
  GratuityFormState,
  GratuityYesNoKey,
  gratuityCustomMaxProblem,
  gratuityFormProblem,
  gratuityOptionSummary,
  gratuityOptionValueProblem,
  labelOf,
  moveGratuityOption,
  newGratuityOptionRow,
  patchGratuityOption,
  removeGratuityOption,
  setDefaultGratuityOption,
} from "./gratuity";
import {
  BORDER_SOFT,
  BORDER_STRONG,
  BottomSheet,
  ConfirmSheet,
  DESTRUCTIVE,
  Field,
  FieldError,
  FieldLabel,
  Icon,
  MobileCard,
  MobileCardList,
  RADIUS_INPUT,
  RADIUS_PILL,
  SegmentedControl,
  SheetField,
  StickyFooter,
  STICKY_FOOTER_HEIGHT,
  TAP,
  TEAL,
  TEAL_TINT_SOFT,
  TEXT,
  TEXT_DIM,
  TEXT_MUTED,
  inputStyle,
  t,
} from "../../components/mobile";

export type CompanyTab = "company" | "billing" | "user" | "location" | "invoice" | "serviceCharge" | "gratuity";

/**
 * Everything the mobile Company screen needs from Company.tsx. The state and
 * the API handlers stay in Company.tsx (shared with desktop); this component
 * only decides how they are laid out on a phone.
 */
export interface CompanyMobileProps {
  language: any;
  tab: string;
  onChangeTab: (tab: string) => void;
  permissions: { billing: boolean; users: boolean; invoice: boolean };
  loading: boolean;

  // Company details
  formValues: any;
  setFormValues: (updater: any) => void;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  errors: any;
  setErrors: (errors: any) => void;
  onSubmitCompany: () => void;
  onCancelCompany: () => void;
  countries: { id: string; code: string; name: string }[];
  searchCountry: string;
  onSelectCountry: (id: string, name: string) => void;
  base64Image: string;
  setBase64Image: (v: any) => void;
  fileName: string;
  setFileName: (v: string) => void;
  handleFileChange: (e: any) => void;

  // Accountant
  accountant: any[] | undefined;
  onRemoveAccountant: (v: any) => void;
  inviteOpen: boolean;
  setInviteOpen: (open: boolean) => void;
  formValuesInvite: any;
  handleInviteChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  errorsAccountant: any;
  setErrorsAccountant: (errors: any) => void;
  onSendInvite: () => void;

  // Billing
  cancelSubscriptionOpen: boolean;
  setCancelSubscriptionOpen: (open: boolean) => void;

  // Users / Locations
  listUser: { data: any[]; count: number } | undefined;
  listLocation: { data: any[]; count: number } | undefined;
  filterUsers: any;
  filterLocations: any;
  onPageUsers: (page: number) => void;
  onPageLocations: (page: number) => void;
  /** Deletes the user / location with `id` (the correct service for `tab`) and refreshes that list. */
  onDeleteItem: (tab: "user" | "location", id: string) => Promise<void> | void;

  // Invoice configurations
  onSaveInvoiceConfig: () => void;
  listBankDetails: { data: any[]; count: number };
  bankModalOpen: boolean;
  openBankModal: (item?: any) => void;
  closeBankModal: () => void;
  editingBankId: string | null;
  bankFormValues: any;
  handleBankFormChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  bankFormErrors: Record<string, string>;
  onSaveBankDetail: () => void;
  onDeleteBankDetail: (id: string) => void;

  // Service charge
  listServiceCharge: any[];
  serviceChargeTotal: number;
  serviceChargeFilter: { page: number; limit: number };
  serviceChargeLoading: boolean;
  showServiceChargeForm: boolean;
  editingServiceChargeId: string | null;
  serviceChargeForm: { chargeType: number; value: string; enable: string; optional: string; taxable: string };
  /** Save errors for the value field (limits / API), keyed "value". */
  serviceChargeErrors?: Record<string, string>;
  setServiceChargeForm: (updater: any) => void;
  setServiceChargeValue: (label: string) => void;
  onAddServiceCharge: () => void;
  onBackServiceCharge: () => void;
  /** Resolves true once the record is loaded into the form; false when the GET failed. */
  onEditServiceCharge: (id: string) => Promise<boolean> | boolean;
  onSaveServiceCharge: () => void;
  onServiceChargePage: (page: number) => void;
  deleteServiceChargeOpen: boolean;
  onAskDeleteServiceCharge: (id: string) => void;
  onCancelDeleteServiceCharge: () => void;
  onConfirmDeleteServiceCharge: () => void;

  // Gratuity
  listGratuity: any[];
  gratuityTotal: number;
  gratuityFilter: { page: number; limit: number };
  gratuityLoading: boolean;
  showGratuityForm: boolean;
  editingGratuityId: string | null;
  gratuityForm: GratuityFormState;
  /** Save errors by API field (limits / API), e.g. customMaxPercent. */
  gratuityErrors?: Record<string, string>;
  setGratuityForm: (updater: any) => void;
  gratuityLocations: { id: string; name: string }[];
  gratuityTaxGroups: { id: string; name: string }[];
  /** "Company default" or the location's name for a list row. */
  gratuityScopeName: (item: any) => string;
  onAddGratuity: () => void;
  onBackGratuity: () => void;
  /** Resolves true once the record is loaded into the form; false when the GET failed. */
  onEditGratuity: (id: string) => Promise<boolean> | boolean;
  onSaveGratuity: () => void;
  onGratuityPage: (page: number) => void;
  deleteGratuityOpen: boolean;
  onAskDeleteGratuity: (id: string) => void;
  onCancelDeleteGratuity: () => void;
  onConfirmDeleteGratuity: () => void;

  /** The page's result popup (PopupLocation on desktop): "Update successfully", API errors. */
  noticeOpen: boolean;
  noticeMessage: string;
  onCloseNotice: () => void;
}

/**
 * After a failed submit: scroll the first invalid field (the one carrying
 * aria-invalid, or a bare inline message) into view and focus it. Runs on the
 * next frame so the inline messages have rendered. `root` limits the search,
 * e.g. to an open sheet.
 */
const scrollToFirstError = (root?: Element | null) => {
  if (typeof window === "undefined") return;
  window.requestAnimationFrame(() => {
    const scope = root ?? document;
    const invalid =
      scope.querySelector<HTMLElement>('[aria-invalid="true"]') ?? scope.querySelector<HTMLElement>('[role="alert"]');
    if (!invalid) return;
    invalid.scrollIntoView({ block: "center", behavior: "smooth" });
    if (invalid.matches("input,select,textarea,button")) invalid.focus({ preventScroll: true });
  });
};

const sectionTitle: React.CSSProperties = {
  fontSize: "17px",
  fontWeight: 600,
  color: TEXT,
  lineHeight: "24px",
  margin: "8px 0 14px",
};

const pillButton = (primary: boolean, destructive?: boolean): React.CSSProperties => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "6px",
  height: `${TAP}px`,
  padding: "0 18px",
  borderRadius: RADIUS_PILL,
  fontSize: "14px",
  fontWeight: 500,
  cursor: "pointer",
  border: `1px solid ${primary ? TEAL : destructive ? DESTRUCTIVE : BORDER_STRONG}`,
  backgroundColor: primary ? TEAL : "#FFFFFF",
  color: primary ? "#FFFFFF" : destructive ? DESTRUCTIVE : TEXT,
  whiteSpace: "nowrap",
});

/** Edit / Delete rows at the bottom of a record sheet. */
const RecordActions: React.FC<{
  language: any;
  onEdit: () => void;
  onDelete: () => void;
}> = ({ language, onEdit, onDelete }) => (
  <div className="flex" style={{ gap: "12px", padding: "16px 20px 4px" }}>
    <button type="button" className="font-inter" onClick={onEdit} style={{ ...pillButton(true), flex: 1, height: "48px" }}>
      {t(language, "Edit")}
    </button>
    <button
      type="button"
      className="font-inter"
      onClick={onDelete}
      style={{ ...pillButton(false, true), flex: 1, height: "48px" }}
    >
      {t(language, "Delete")}
    </button>
  </div>
);

const CompanyMobile: React.FC<CompanyMobileProps> = (p) => {
  const { language, tab, formValues, errors } = p;
  const preLanguage = localStorage.getItem("language");

  const [countryOpen, setCountryOpen] = useState(false);
  const [countryQuery, setCountryQuery] = useState("");
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [removeAccountant, setRemoveAccountant] = useState<any>(null);
  const [recordUser, setRecordUser] = useState<any>(null);
  const [recordLocation, setRecordLocation] = useState<any>(null);
  const [deleteItem, setDeleteItem] = useState<{ tab: "user" | "location"; item: any } | null>(null);
  const [recordBank, setRecordBank] = useState<any>(null);
  const [deleteBank, setDeleteBank] = useState<any>(null);
  const [recordCharge, setRecordCharge] = useState<any>(null);
  const [chargeSheetOpen, setChargeSheetOpen] = useState(false);
  const [deleteChargeItem, setDeleteChargeItem] = useState<any>(null);
  const [chargeTouched, setChargeTouched] = useState(false);
  const [recordGratuity, setRecordGratuity] = useState<any>(null);
  const [gratuitySheetOpen, setGratuitySheetOpen] = useState(false);
  const [deleteGratuityItem, setDeleteGratuityItem] = useState<any>(null);
  const [gratuityTouched, setGratuityTouched] = useState(false);
  const pageRef = useRef<HTMLDivElement>(null);
  const companyLimits = useFieldLimits(coreFormLimits("company"));
  const bankLimits = useFieldLimits(coreFormLimits("company-bank-detail"));
  const serviceChargeLimits = useFieldLimits(coreFormLimits("service-charge"));
  const gratuityLimits = useFieldLimits(coreFormLimits("gratuity"));
  const chargeSheetRef = useRef<HTMLDivElement>(null);
  const gratuitySheetRef = useRef<HTMLDivElement>(null);
  const inviteSheetRef = useRef<HTMLDivElement>(null);
  const bankSheetRef = useRef<HTMLDivElement>(null);
  const countryListRef = useRef<HTMLDivElement>(null);

  // Company.tsx closes its own form flag after a successful save; follow it.
  useEffect(() => {
    if (!p.showServiceChargeForm) setChargeSheetOpen(false);
  }, [p.showServiceChargeForm]);
  useEffect(() => {
    if (!p.showGratuityForm) setGratuitySheetOpen(false);
  }, [p.showGratuityForm]);

  useEffect(() => {
    setTouched({});
  }, [tab]);

  // The country sheet opens with the selected option in view, not at the top.
  useEffect(() => {
    if (!countryOpen) return;
    const selected = countryListRef.current?.querySelector<HTMLElement>('[role="option"][aria-selected="true"]');
    selected?.scrollIntoView({ block: "center" });
  }, [countryOpen]);

  const tabs = useMemo(
    () =>
      [
        { value: "company", label: t(language, "Company") },
        p.permissions.billing && { value: "billing", label: t(language, "Billing") },
        p.permissions.users && { value: "user", label: t(language, "Users") },
        { value: "location", label: t(language, "Locations") },
        p.permissions.invoice && { value: "invoice", label: t(language, "Invoices") },
        p.permissions.invoice && { value: "serviceCharge", label: t(language, "Service charge") },
        p.permissions.invoice && { value: "gratuity", label: t(language, "Gratuity") },
      ].filter(Boolean) as { value: string; label: string }[],
    [language, p.permissions],
  );

  /* ---------------- Company details validation ---------------- */

  // The product rule (Company.tsx handleSubmit / handleSaveInvoiceConfig):
  // only Invoice Prefix is required; everything else is optional.
  const companyErrors = useMemo(() => {
    const e: Record<string, string> = validateAgainstLimits(formValues ?? {}, companyLimits, language);
    if (!String(formValues?.invoicePrefix ?? "").trim()) e.invoicePrefix = t(language, "Invoice Prefix is required");
    return e;
  }, [formValues, language, companyLimits]);

  const companyValid = Object.keys(companyErrors).length === 0;
  const fieldError = (name: string): string | undefined =>
    (touched[name] || touched.__all) ? companyErrors[name] ?? errors?.[name] : errors?.[name];
  const markTouched = (name: string) => setTouched((prev) => ({ ...prev, [name]: true }));
  // Field ends in a 16px margin: pull the counter up under its input; render no box while
  // the counter is hidden (FieldCounter's 80% threshold) so the Field's margin still collapses.
  const fieldCounter = (value: unknown, max?: number) =>
    max && [...String(value ?? "")].length >= Math.floor(max * 0.8) ? (
      <div style={{ marginTop: "-12px", paddingBottom: "12px" }}>
        <FieldCounter value={value as string} max={max} />
      </div>
    ) : null;

  // Save stays enabled; an invalid submit marks every field touched so the
  // inline messages show, then scrolls the first one into view.
  const submitCompany = () => {
    setTouched({ __all: true });
    if (!companyValid) {
      p.setErrors(companyErrors);
      scrollToFirstError(pageRef.current);
      return;
    }
    p.setErrors({});
    p.onSubmitCompany();
  };

  const submitInvoiceConfig = () => {
    setTouched({ __all: true });
    if (companyErrors.invoicePrefix) {
      p.setErrors(companyErrors);
      scrollToFirstError(pageRef.current);
      return;
    }
    p.setErrors({});
    p.onSaveInvoiceConfig();
  };

  const cancelCompany = () => {
    setTouched({});
    p.setErrors({});
    p.setBase64Image("");
    p.setFileName("");
    p.onCancelCompany();
  };

  const field = (
    name: string,
    label: string,
    extra: Partial<React.InputHTMLAttributes<HTMLInputElement>> = {},
    required?: boolean,
  ) => (
    <>
      <Field
        id={`company-${name}`}
        name={name}
        label={label}
        required={required}
        value={formValues?.[name] ?? ""}
        onChange={p.handleChange}
        onBlur={() => markTouched(name)}
        error={fieldError(name)}
        {...limitProps(companyLimits, name)}
        {...extra}
      />
      {fieldCounter(formValues?.[name], companyLimits[name]?.maxLength)}
    </>
  );

  /* ---------------- Country sheet ---------------- */

  const filteredCountries = useMemo(() => {
    const q = countryQuery.trim().toLowerCase();
    return q ? p.countries.filter((c) => c.name.toLowerCase().includes(q)) : p.countries;
  }, [countryQuery, p.countries]);

  const countrySheet = (
    <BottomSheet
      open={countryOpen}
      onClose={() => setCountryOpen(false)}
      title={t(language, "Country")}
      header={
        <div style={{ padding: "0 20px 12px" }}>
          <input
            type="search"
            autoFocus
            aria-label={t(language, "Search country")}
            placeholder={t(language, "Search country")}
            value={countryQuery}
            onChange={(e) => setCountryQuery(e.target.value)}
            className="font-inter placeholder:text-placeholder"
            style={inputStyle}
          />
        </div>
      }
    >
      <div ref={countryListRef} role="listbox" aria-label={t(language, "Country")}>
        {filteredCountries.map((c) => {
          const selected = c.id === formValues?.countryId;
          return (
            <button
              key={c.id}
              type="button"
              role="option"
              aria-selected={selected}
              className="flex items-center font-inter"
              onClick={() => {
                p.onSelectCountry(c.id, c.name);
                setCountryOpen(false);
              }}
              style={{
                width: "100%",
                minHeight: "48px",
                padding: "0 20px",
                gap: "12px",
                border: "none",
                borderBottom: `1px solid ${BORDER_SOFT}`,
                backgroundColor: selected ? TEAL_TINT_SOFT : "transparent",
                textAlign: "left",
                fontSize: "15px",
                color: TEXT,
                cursor: "pointer",
              }}
            >
              <span style={{ flex: 1, minWidth: 0 }}>{c.name}</span>
              {selected && <Icon name="check" size={18} color={TEAL} />}
            </button>
          );
        })}
        {filteredCountries.length === 0 && (
          <p className="font-inter" style={{ padding: "16px 20px", fontSize: "14px", color: TEXT_DIM }}>
            {t(language, "No countries match")}
          </p>
        )}
      </div>
    </BottomSheet>
  );

  /* ---------------- Company details tab ---------------- */

  const logoSrc = p.base64Image || formValues?.companyLogo;

  const companyTab = (
    <form onSubmit={(e) => { e.preventDefault(); submitCompany(); }} noValidate>
      <p className="font-inter" style={sectionTitle}>{t(language, "General details")}</p>
      {field("companyName", t(language, "Company Name"), { autoComplete: "organization" })}
      {field("companyNumber", t(language, "Company Number"), { inputMode: "numeric" })}
      {field("fiscalNumber", t(language, "Fiscal Number"), { inputMode: "numeric" })}
      {field("taxCode", t(language, "TAX Code"), { inputMode: "numeric" })}
      {field("webUrl", t(language, "Web URL"), { type: "url", inputMode: "url", autoComplete: "url" })}

      <p className="font-inter" style={sectionTitle}>{t(language, "Address")}</p>
      {field("addressLine1", t(language, "Address line 1"), { autoComplete: "address-line1" })}
      {field("addressLine2", t(language, "Address line 2"), { autoComplete: "address-line2" })}
      {field("city", t(language, "City"), { autoComplete: "address-level2" })}
      {field("postalCode", t(language, "Postal Code"), { inputMode: "numeric", autoComplete: "postal-code" })}
      <div style={{ marginBottom: "16px" }}>
        <FieldLabel htmlFor="company-country">{t(language, "Country")}</FieldLabel>
        <button
          id="company-country"
          type="button"
          aria-haspopup="dialog"
          className="flex items-center font-inter"
          onClick={() => {
            setCountryQuery("");
            setCountryOpen(true);
          }}
          style={{ ...inputStyle, textAlign: "left", cursor: "pointer", display: "flex", gap: "8px" }}
        >
          <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: p.searchCountry ? TEXT : TEXT_DIM }}>
            {p.searchCountry || t(language, "Select country")}
          </span>
          <Icon name="chevd" size={18} />
        </button>
        {errors?.countryId && <FieldError>{errors.countryId}</FieldError>}
      </div>

      <p className="font-inter" style={sectionTitle}>{t(language, "Contact details")}</p>
      {field("emailAddress", t(language, "Email address"), { type: "email", inputMode: "email", autoComplete: "email" })}
      {field("phoneNumber", t(language, "Phone Number"), { type: "tel", inputMode: "tel", autoComplete: "tel" })}
      {field("invoicePrefix", t(language, "Invoice Prefix"), { placeholder: t(language, "e.g. INV-") }, true)}

      <p className="font-inter" style={sectionTitle}>{t(language, "Branding")}</p>
      <div style={{ marginBottom: "16px" }}>
        <FieldLabel>{t(language, "Company logo")}</FieldLabel>
        <div
          className="flex items-center"
          style={{ gap: "12px", padding: "8px 12px", border: `1px solid ${BORDER_STRONG}`, borderRadius: "12px" }}
        >
          <div
            className="flex items-center justify-center"
            style={{ width: "64px", height: "64px", borderRadius: "999px", backgroundColor: TEAL_TINT_SOFT, flex: "none", overflow: "hidden" }}
          >
            {logoSrc ? (
              <img src={logoSrc} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
            ) : (
              <PhotoIcon />
            )}
          </div>
          <span
            className="font-inter"
            style={{ flex: 1, minWidth: 0, fontSize: "13px", color: TEXT_DIM, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {p.fileName || (logoSrc ? t(language, "Current logo") : t(language, "No logo yet"))}
          </span>
          <label htmlFor="company-logo-input" className="font-inter" style={{ ...pillButton(false), padding: "0 14px" }}>
            {logoSrc ? t(language, "Change") : t(language, "Upload")}
          </label>
          <input id="company-logo-input" type="file" accept="image/*" onChange={p.handleFileChange} style={{ display: "none" }} />
          {logoSrc && (
            <button
              type="button"
              aria-label={t(language, "Remove logo")}
              className="flex items-center justify-center"
              onClick={() => {
                p.setBase64Image("");
                p.setFileName("");
                p.setFormValues((prev: any) => ({ ...prev, companyLogo: "" }));
              }}
              style={{ width: `${TAP}px`, height: `${TAP}px`, border: "none", background: "transparent", cursor: "pointer", marginRight: "-8px" }}
            >
              <Icon name="close" size={18} color={DESTRUCTIVE} />
            </button>
          )}
        </div>
      </div>

      <div style={{ padding: "20px 16px", backgroundColor: TEAL_TINT_SOFT, borderRadius: "16px", marginTop: "8px" }}>
        <p className="font-inter" style={{ fontSize: "17px", fontWeight: 600, color: TEXT, lineHeight: "24px" }}>
          {t(language, "Invite your accountant")}
        </p>
        <p className="font-inter" style={{ fontSize: "14px", color: TEXT_MUTED, lineHeight: "21px", marginTop: "6px" }}>
          {t(language, "Easily share access with your accountant so they can review reports and manage finances")}
        </p>
        {p.accountant?.map((v: any) => (
          <div key={v?.userId ?? v?.user?.emailAddress} className="flex items-center" style={{ gap: "12px", marginTop: "14px" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p className="font-inter" style={{ fontSize: "15px", fontWeight: 500, color: TEXT, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {v?.user?.firstName} {v?.user?.lastName}
              </p>
              <p className="font-inter" style={{ fontSize: "13px", color: TEXT_DIM, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {v?.user?.emailAddress}
              </p>
            </div>
            <button type="button" className="font-inter" onClick={() => setRemoveAccountant(v)} style={pillButton(false, true)}>
              {t(language, "Remove")}
            </button>
          </div>
        ))}
        <button
          type="button"
          className="font-inter"
          onClick={() => {
            p.setErrorsAccountant({});
            p.setInviteOpen(true);
          }}
          style={{ ...pillButton(true), marginTop: "16px" }}
        >
          <span style={{ fontSize: "18px", lineHeight: 1 }}>+</span>
          {t(language, "Invite accountant")}
        </button>
      </div>
    </form>
  );

  const inviteSheet = (
    <BottomSheet
      open={p.inviteOpen}
      onClose={() => p.setInviteOpen(false)}
      title={t(language, "Invite accountant")}
      footer={
        <StickyFooter
          position="static"
          primaryLabel={t(language, "Send invite")}
          onPrimary={() => {
            p.onSendInvite();
            scrollToFirstError(inviteSheetRef.current);
          }}
          cancelLabel={t(language, "Cancel")}
          onCancel={() => p.setInviteOpen(false)}
        />
      }
    >
      <div ref={inviteSheetRef} style={{ padding: "0 20px" }}>
        <Field
          label={t(language, "First Name")}
          required
          name="firstName"
          autoComplete="given-name"
          value={p.formValuesInvite?.firstName ?? ""}
          onChange={p.handleInviteChange}
          error={p.errorsAccountant?.firstName}
        />
        <Field
          label={t(language, "Last Name")}
          required
          name="lastName"
          autoComplete="family-name"
          value={p.formValuesInvite?.lastName ?? ""}
          onChange={p.handleInviteChange}
          error={p.errorsAccountant?.lastName}
        />
        <Field
          label={t(language, "Email address")}
          required
          name="emailAddress"
          type="email"
          inputMode="email"
          autoComplete="email"
          value={p.formValuesInvite?.emailAddress ?? ""}
          onChange={p.handleInviteChange}
          error={p.errorsAccountant?.emailAddress}
        />
      </div>
    </BottomSheet>
  );

  /* ---------------- Billing tab ---------------- */

  const billingRows = [
    { id: "", "Invoice No": "#123123", Title: "Service for air conditioning", Category: "Category", Date: "01/01/2025", Status: "Unpaid" },
  ];

  const billingTab = (
    <div>
      <p className="font-inter" style={sectionTitle}>{t(language, "Billing history")}</p>
      <MobileCardList>
        {billingRows.map((row, i) => (
          <MobileCard
            key={row["Invoice No"]}
            title={row.Title}
            subtitle={`${row["Invoice No"]} · ${row.Category}`}
            subtitle2={row.Date}
            trailing={t(language, row.Status)}
            last={i === billingRows.length - 1}
          />
        ))}
      </MobileCardList>
      <div style={{ marginTop: "24px", padding: "16px", backgroundColor: "#F8F8F8", border: `1px solid ${BORDER_SOFT}`, borderRadius: "12px" }}>
        <p className="font-inter" style={{ fontSize: "15px", fontWeight: 500, color: TEXT }}>
          {t(language, "Cancel Your Subscription")}
        </p>
        <p className="font-inter" style={{ fontSize: "14px", color: TEXT_MUTED, lineHeight: "20px", marginTop: "4px" }}>
          {t(language, "Stop your premium account billing anytime. You can resume your subscription whenever you want.")}
        </p>
        <button
          type="button"
          className="font-inter"
          onClick={() => p.setCancelSubscriptionOpen(true)}
          style={{ ...pillButton(false, true), marginTop: "14px" }}
        >
          {t(language, "Cancel Subscription")}
        </button>
      </div>
    </div>
  );

  /* ---------------- Users / Locations ---------------- */

  const pager = (page: number, limit: number, count: number, onPage: (page: number) => void) => {
    const totalPages = Math.max(1, Math.ceil((count || 0) / (limit || 1)));
    if (!count) return null;
    const from = (page - 1) * limit + 1;
    const to = Math.min(page * limit, count);
    const btn = (label: string, disabled: boolean, onClick: () => void, flip?: boolean) => (
      <button
        type="button"
        aria-label={label}
        disabled={disabled}
        onClick={onClick}
        className="flex items-center justify-center"
        style={{
          width: `${TAP}px`,
          height: `${TAP}px`,
          borderRadius: "12px",
          border: `1px solid ${BORDER_STRONG}`,
          backgroundColor: "#FFFFFF",
          opacity: disabled ? 0.4 : 1,
          transform: flip ? "rotate(180deg)" : undefined,
          cursor: disabled ? "default" : "pointer",
        }}
      >
        <Icon name="chev" size={16} color={TEXT} />
      </button>
    );
    return (
      <div className="flex items-center justify-between" style={{ padding: "12px 0 8px" }}>
        <span className="font-inter" style={{ fontSize: "13px", color: TEXT_MUTED }}>
          {t(language, "Showing")} {from}–{to} {t(language, "of")} {count}
        </span>
        <div className="flex" style={{ gap: "8px" }}>
          {btn(t(language, "Previous page"), page <= 1, () => onPage(page - 1), true)}
          {btn(t(language, "Next page"), page >= totalPages, () => onPage(page + 1))}
        </div>
      </div>
    );
  };

  const emptyList = (text: string) => (
    <p className="font-inter" style={{ padding: "24px 0", fontSize: "14px", color: TEXT_DIM, textAlign: "center" }}>
      {text}
    </p>
  );

  const entityList = (
    which: "user" | "location",
    list: { data: any[]; count: number } | undefined,
    filter: any,
    onPage: (page: number) => void,
    setRecord: (item: any) => void,
  ) => {
    const rows = list?.data ?? [];
    return (
      <div>
        <p className="font-inter" style={sectionTitle}>
          {which === "user" ? t(language, "User Management") : t(language, "Locations")}
        </p>
        <div style={{ position: "relative", minHeight: p.loading ? "200px" : undefined }}>
          {p.loading && <LoadingV2 />}
          {!p.loading && rows.length === 0 && emptyList(which === "user" ? t(language, "No users yet") : t(language, "No locations yet"))}
          <MobileCardList>
            {rows.map((item: any, i: number) => (
              <MobileCard
                key={item.id ?? i}
                title={item.Name}
                subtitle={[item.Code, item.Email].filter(Boolean).join(" · ")}
                subtitle2={which === "user" ? item.Role : undefined}
                trailing={t(language, item.Status)}
                onClick={() => setRecord(item)}
                last={i === rows.length - 1}
              />
            ))}
          </MobileCardList>
        </div>
        {pager(filter?.page || 1, filter?.limit || 50, list?.count || 0, onPage)}
      </div>
    );
  };

  const entityPath = (which: "user" | "location") => (which === "user" ? "/companies/user/" : "/companies/location/");

  const recordSheet = (which: "user" | "location", record: any, close: () => void) => (
    <BottomSheet open={!!record} onClose={close} title={record?.Name ?? ""}>
      {record && (
        <>
          <div className="flex flex-col" style={{ gap: "6px", padding: "0 20px" }}>
            <SheetField label={t(language, "Code")} value={record.Code ?? "/"} />
            <SheetField label={t(language, "Email")} value={record.Email ?? "/"} />
            {which === "user" && <SheetField label={t(language, "Role")} value={record.Role ?? "/"} />}
            <SheetField label={t(language, "Status")} value={t(language, record.Status)} />
          </div>
          <RecordActions
            language={language}
            onEdit={() => {
              close();
              webNavigate("/" + preLanguage + entityPath(which) + record.id);
            }}
            onDelete={() => {
              close();
              setDeleteItem({ tab: which, item: record });
            }}
          />
        </>
      )}
    </BottomSheet>
  );

  /* ---------------- Invoice configurations ---------------- */

  const bankLine = (bank: any) => {
    const main = bank.IBAN || bank.accountNo || "";
    return [main, bank.SWIFT].filter(Boolean).join(" · ");
  };

  const invoiceTab = (
    <form onSubmit={(e) => { e.preventDefault(); submitInvoiceConfig(); }} noValidate>
      <p className="font-inter" style={sectionTitle}>{t(language, "Invoice configurations")}</p>
      <p className="font-inter" style={{ fontSize: "14px", color: TEXT_MUTED, lineHeight: "21px", marginBottom: "16px" }}>
        {t(language, "Manage how invoice numbers and prefixes are set for this company. These settings are used when creating invoices.")}
      </p>
      {field("invoicePrefix", t(language, "Invoice Prefix"), { placeholder: t(language, "e.g. INV-") }, true)}

      <div className="flex items-center" style={{ gap: "12px", margin: "24px 0 6px" }}>
        <p className="font-inter" style={{ ...sectionTitle, margin: 0, flex: 1 }}>{t(language, "Bank details")}</p>
        <button type="button" className="font-inter" onClick={() => p.openBankModal()} style={pillButton(true)}>
          <span style={{ fontSize: "18px", lineHeight: 1 }}>+</span>
          {t(language, "Add")}
        </button>
      </div>
      <p className="font-inter" style={{ fontSize: "14px", color: TEXT_MUTED, lineHeight: "21px", marginBottom: "8px" }}>
        {t(language, "Add bank accounts to show on invoices (e.g. for payment instructions).")}
      </p>
      {p.listBankDetails.data?.length > 0 ? (
        <MobileCardList>
          {p.listBankDetails.data.map((bank: any, i: number) => (
            <MobileCard
              key={bank.id}
              title={bank.bankName}
              subtitle={bankLine(bank) || undefined}
              subtitle2={bank.bankLocation || undefined}
              onClick={() => setRecordBank(bank)}
              last={i === p.listBankDetails.data.length - 1}
            />
          ))}
        </MobileCardList>
      ) : (
        emptyList(t(language, "No bank details yet. Add one to display on invoices."))
      )}
    </form>
  );

  const bankRecordSheet = (
    <BottomSheet open={!!recordBank} onClose={() => setRecordBank(null)} title={recordBank?.bankName ?? ""}>
      {recordBank && (
        <>
          <div className="flex flex-col" style={{ gap: "6px", padding: "0 20px" }}>
            <SheetField label={t(language, "IBAN")} value={recordBank.IBAN || "/"} />
            <SheetField label={t(language, "SWIFT")} value={recordBank.SWIFT || "/"} />
            <SheetField label={t(language, "Account number")} value={recordBank.accountNo || "/"} />
            <SheetField label={t(language, "Bank location")} value={recordBank.bankLocation || "/"} />
          </div>
          <RecordActions
            language={language}
            onEdit={() => {
              const bank = recordBank;
              setRecordBank(null);
              p.openBankModal(bank);
            }}
            onDelete={() => {
              const bank = recordBank;
              setRecordBank(null);
              setDeleteBank(bank);
            }}
          />
        </>
      )}
    </BottomSheet>
  );

  const bankField = (name: string, label: string, extra: Partial<React.InputHTMLAttributes<HTMLInputElement>> = {}, required?: boolean) => (
    <>
      <Field
        label={label}
        required={required}
        name={name}
        value={p.bankFormValues?.[name] ?? ""}
        onChange={p.handleBankFormChange}
        error={p.bankFormErrors?.[name] || undefined}
        {...limitProps(bankLimits, name)}
        {...extra}
      />
      {fieldCounter(p.bankFormValues?.[name], bankLimits[name]?.maxLength)}
    </>
  );

  const bankSheet = (
    <BottomSheet
      open={p.bankModalOpen}
      onClose={p.closeBankModal}
      title={p.editingBankId ? t(language, "Edit bank detail") : t(language, "Add bank detail")}
      footer={
        <StickyFooter
          position="static"
          primaryLabel={t(language, "Save")}
          onPrimary={() => {
            p.onSaveBankDetail();
            scrollToFirstError(bankSheetRef.current);
          }}
          cancelLabel={t(language, "Cancel")}
          onCancel={p.closeBankModal}
        />
      }
    >
      <div ref={bankSheetRef} style={{ padding: "0 20px" }}>
        {bankField("bankName", t(language, "Bank name"), { placeholder: t(language, "e.g. My Bank") }, true)}
        {bankField("IBAN", t(language, "IBAN"), { placeholder: "e.g. XX00 0000 0000 0000 0000 0000", autoCapitalize: "characters" })}
        {bankField("SWIFT", t(language, "SWIFT"), { placeholder: "e.g. XXXXXXXX", autoCapitalize: "characters" })}
        {bankField("accountNo", t(language, "Account number"), { inputMode: "numeric" })}
        {bankField("bankLocation", t(language, "Bank location"), { placeholder: t(language, "e.g. City, Country") })}
      </div>
    </BottomSheet>
  );

  /* ---------------- Service charge ---------------- */

  const chargeValueLabel = (item: any) => (item.chargeType === 1 ? `${item.value}%` : String(item.value));
  const chargeFlags = (item: any) =>
    [
      item.isEnabled ? t(language, "Enabled") : t(language, "Disabled"),
      item.isOptional ? t(language, "Optional") : null,
      item.isTaxable ? t(language, "Taxable") : null,
    ]
      .filter(Boolean)
      .join(" · ");

  const openChargeSheet = () => {
    setChargeTouched(false);
    setChargeSheetOpen(true);
  };

  const closeChargeSheet = () => {
    setChargeSheetOpen(false);
    p.onBackServiceCharge();
  };

  const serviceChargeTab = (
    <div>
      <p className="font-inter" style={sectionTitle}>{t(language, "Service charge")}</p>
      <div style={{ position: "relative", minHeight: p.serviceChargeLoading ? "200px" : undefined }}>
        {p.serviceChargeLoading && <LoadingV2 />}
        {!p.serviceChargeLoading && p.listServiceCharge.length === 0 && emptyList(t(language, "No service charge yet"))}
        <MobileCardList>
          {p.listServiceCharge.map((item: any, i: number) => (
            <MobileCard
              key={item.id}
              title={`${t(language, item.chargeTypeText)} · ${chargeValueLabel(item)}`}
              subtitle={chargeFlags(item)}
              onClick={() => setRecordCharge(item)}
              last={i === p.listServiceCharge.length - 1}
            />
          ))}
        </MobileCardList>
      </div>
      {/* A company may hold at most one: say so where the Add button used to lead. */}
      {!p.serviceChargeLoading && p.listServiceCharge.length > 0 && (
        <p className="font-inter" style={{ padding: "12px 0 0", fontSize: "13px", lineHeight: "18px", color: TEXT_DIM }}>
          {t(language, "A company can have only one service charge.")}
        </p>
      )}
      {/* One row at most, so the pager only appears if the API ever returns more. */}
      {!p.serviceChargeLoading &&
        p.serviceChargeTotal > p.serviceChargeFilter.limit &&
        pager(p.serviceChargeFilter.page, p.serviceChargeFilter.limit, p.serviceChargeTotal, p.onServiceChargePage)}
    </div>
  );

  const chargeRecordSheet = (
    <BottomSheet
      open={!!recordCharge}
      onClose={() => setRecordCharge(null)}
      title={recordCharge ? `${t(language, recordCharge.chargeTypeText)} · ${chargeValueLabel(recordCharge)}` : ""}
    >
      {recordCharge && (
        <>
          <div className="flex flex-col" style={{ gap: "6px", padding: "0 20px" }}>
            <SheetField label={t(language, "Charge type")} value={t(language, recordCharge.chargeTypeText)} />
            <SheetField label={t(language, "Value")} value={chargeValueLabel(recordCharge)} />
            <SheetField label={t(language, "Enable")} value={recordCharge.isEnabled ? t(language, "Yes") : t(language, "No")} />
            <SheetField label={t(language, "Make it optional")} value={recordCharge.isOptional ? t(language, "Yes") : t(language, "No")} />
            <SheetField label={t(language, "Taxable")} value={recordCharge.isTaxable ? t(language, "Yes") : t(language, "No")} />
          </div>
          <RecordActions
            language={language}
            onEdit={async () => {
              const id = recordCharge.id;
              setRecordCharge(null);
              // Open only once the record has loaded (editingServiceChargeId
              // and the form are set by then); on failure stay closed and let
              // Company.tsx's toast show.
              const loaded = await p.onEditServiceCharge(id);
              if (loaded) openChargeSheet();
            }}
            onDelete={() => {
              const item = recordCharge;
              setRecordCharge(null);
              setDeleteChargeItem(item);
              p.onAskDeleteServiceCharge(item.id);
            }}
          />
        </>
      )}
    </BottomSheet>
  );

  const chargeValueNumber = Number(p.serviceChargeForm.value);
  const chargeValueError =
    p.serviceChargeForm.value === "" || Number.isNaN(chargeValueNumber)
      ? t(language, "Value is required")
      : undefined;

  const yesNo = (key: "enable" | "optional" | "taxable", label: string) => (
    <div style={{ marginBottom: "16px" }}>
      <FieldLabel>{label}</FieldLabel>
      <SegmentedControl
        ariaLabel={label}
        options={[
          { value: "yes", label: t(language, "Yes") },
          { value: "no", label: t(language, "No") },
        ]}
        value={p.serviceChargeForm[key]}
        onChange={(v) => p.setServiceChargeForm((prev: any) => ({ ...prev, [key]: v }))}
      />
    </div>
  );

  const chargeSheet = (
    <BottomSheet
      open={chargeSheetOpen}
      onClose={closeChargeSheet}
      title={p.editingServiceChargeId ? t(language, "Edit service charge") : t(language, "Add service charge")}
      footer={
        <StickyFooter
          position="static"
          primaryLabel={t(language, "Save")}
          onPrimary={() => {
            setChargeTouched(true);
            if (chargeValueError) {
              scrollToFirstError(chargeSheetRef.current);
              return;
            }
            p.onSaveServiceCharge();
          }}
          cancelLabel={t(language, "Cancel")}
          onCancel={closeChargeSheet}
        />
      }
    >
      <div ref={chargeSheetRef} style={{ padding: "0 20px" }}>
        <div style={{ marginBottom: "16px" }}>
          <FieldLabel>{t(language, "Charge type")}</FieldLabel>
          <SegmentedControl
            ariaLabel={t(language, "Charge type")}
            options={[
              { value: "1", label: t(language, "Percentage") },
              { value: "2", label: t(language, "Fixed") },
            ]}
            value={String(p.serviceChargeForm.chargeType)}
            onChange={(v) => {
              p.setServiceChargeForm((prev: any) => ({ ...prev, chargeType: Number(v) }));
              p.setServiceChargeValue(v === "2" ? "Fixed" : "Percentage");
            }}
          />
        </div>
        <Field
          label={t(language, "Value")}
          required
          name="value"
          inputMode="decimal"
          placeholder={p.serviceChargeForm.chargeType === 1 ? "10" : "2.50"}
          value={p.serviceChargeForm.value}
          {...limitProps(serviceChargeLimits, p.serviceChargeForm.chargeType === 2 ? "fixedAmount" : "percentageValue", "number")}
          onChange={(e) => p.setServiceChargeForm((prev: any) => ({ ...prev, value: e.target.value }))}
          onBlur={() => setChargeTouched(true)}
          error={(chargeTouched ? chargeValueError : undefined) ?? p.serviceChargeErrors?.value}
        />
        {yesNo("enable", t(language, "Enable"))}
        {yesNo("optional", t(language, "Make it optional"))}
        {yesNo("taxable", t(language, "Taxable"))}
      </div>
    </BottomSheet>
  );

  /* ---------------- Gratuity ---------------- */

  const tr = (key: string) => t(language, key);
  const yesNoText = (value: boolean) => (value ? tr("Yes") : tr("No"));

  const gratuityFlags = (item: any) =>
    [
      item.isEnabled ? tr("Enabled") : tr("Disabled"),
      item.allowCustomGratuity ? tr("Custom allowed") : null,
      item.isTaxable ? tr("Taxable") : null,
    ]
      .filter(Boolean)
      .join(" · ");

  const openGratuitySheet = () => {
    setGratuityTouched(false);
    setGratuitySheetOpen(true);
  };

  const closeGratuitySheet = () => {
    setGratuitySheetOpen(false);
    p.onBackGratuity();
  };

  const gratuityTab = (
    <div>
      <p className="font-inter" style={sectionTitle}>{tr("Gratuity")}</p>
      <div style={{ position: "relative", minHeight: p.gratuityLoading ? "200px" : undefined }}>
        {p.gratuityLoading && <LoadingV2 />}
        {!p.gratuityLoading && p.listGratuity.length === 0 && emptyList(tr("No gratuity settings yet"))}
        <MobileCardList>
          {p.listGratuity.map((item: any, i: number) => (
            <MobileCard
              key={item.id}
              title={p.gratuityScopeName(item)}
              subtitle={gratuityOptionSummary(item.options, tr)}
              subtitle2={gratuityFlags(item)}
              onClick={() => setRecordGratuity(item)}
              last={i === p.listGratuity.length - 1}
            />
          ))}
        </MobileCardList>
      </div>
      {!p.gratuityLoading &&
        p.gratuityTotal > p.gratuityFilter.limit &&
        pager(p.gratuityFilter.page, p.gratuityFilter.limit, p.gratuityTotal, p.onGratuityPage)}
    </div>
  );

  const gratuityRecordSheet = (
    <BottomSheet
      open={!!recordGratuity}
      onClose={() => setRecordGratuity(null)}
      title={recordGratuity ? p.gratuityScopeName(recordGratuity) : ""}
    >
      {recordGratuity && (
        <>
          <div className="flex flex-col" style={{ gap: "6px", padding: "0 20px" }}>
            <SheetField label={tr("Options")} value={gratuityOptionSummary(recordGratuity.options, tr)} />
            <SheetField label={tr("Enable")} value={yesNoText(recordGratuity.isEnabled)} />
            <SheetField
              label={tr("Allow custom gratuity")}
              value={
                recordGratuity.allowCustomGratuity
                  ? recordGratuity.customMaxPercent !== null
                    ? `${tr("Yes")} · ${tr("Maximum custom %")} ${recordGratuity.customMaxPercent}`
                    : tr("Yes")
                  : tr("No")
              }
            />
            <SheetField label={tr("Offer cash change as gratuity")} value={yesNoText(recordGratuity.changeToGratuityEnabled)} />
            <SheetField label={tr("Prompt mode")} value={tr(labelOf(GRATUITY_PROMPT_MODES, recordGratuity.promptMode))} />
            <SheetField label={tr("Percent base")} value={tr(labelOf(GRATUITY_PERCENT_BASES, recordGratuity.percentBase))} />
            <SheetField label={tr("Taxable")} value={recordGratuity.isTaxable ? recordGratuity.taxGroupName || tr("Yes") : tr("No")} />
            <SheetField label={tr("Round up to the next step when already exact")} value={yesNoText(recordGratuity.roundUpExactToNext)} />
          </div>
          <RecordActions
            language={language}
            onEdit={async () => {
              const id = recordGratuity.id;
              setRecordGratuity(null);
              // Open only once the record has loaded; on failure Company.tsx's toast shows.
              const loaded = await p.onEditGratuity(id);
              if (loaded) openGratuitySheet();
            }}
            onDelete={() => {
              const item = recordGratuity;
              setRecordGratuity(null);
              setDeleteGratuityItem(item);
              p.onAskDeleteGratuity(item.id);
            }}
          />
        </>
      )}
    </BottomSheet>
  );

  const gratuityProblem = gratuityFormProblem(p.gratuityForm);
  const gratuityCustomMaxError = gratuityTouched ? gratuityCustomMaxProblem(p.gratuityForm) : null;
  const gratuityTaxGroupError =
    gratuityTouched && p.gratuityForm.taxable === "yes" && !p.gratuityForm.taxGroupId
      ? "Select a tax group for a taxable gratuity"
      : null;
  // Rules about the list as a whole; per-row and per-field ones show at the field.
  const gratuityListError =
    gratuityTouched &&
    (gratuityProblem === "Add at least one gratuity option" || gratuityProblem === "Only one option can be the default")
      ? gratuityProblem
      : null;

  const gratuityYesNo = (key: GratuityYesNoKey, label: string) => (
    <div style={{ marginBottom: "16px" }}>
      <FieldLabel>{label}</FieldLabel>
      <SegmentedControl
        ariaLabel={label}
        options={[
          { value: "yes", label: tr("Yes") },
          { value: "no", label: tr("No") },
        ]}
        value={p.gratuityForm[key]}
        onChange={(v) => p.setGratuityForm((prev: GratuityFormState) => ({ ...prev, [key]: v }))}
      />
    </div>
  );

  const gratuitySelect = (
    label: string,
    value: string,
    onChange: (value: string) => void,
    options: { id: string; name: string }[],
    placeholder?: string,
    error?: string | null,
  ) => (
    <div style={{ marginBottom: "16px" }}>
      <FieldLabel>{label}</FieldLabel>
      <div style={{ position: "relative" }}>
        <select
          className="font-inter"
          aria-label={label}
          aria-invalid={!!error || undefined}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{ ...inputStyle, paddingRight: "40px", ...(error ? { borderColor: DESTRUCTIVE } : {}) }}
        >
          {placeholder !== undefined && <option value="">{placeholder}</option>}
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </select>
        <span style={{ position: "absolute", right: "14px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none", display: "flex" }}>
          <Icon name="chevd" size={18} />
        </span>
      </div>
      {error && <FieldError>{tr(error)}</FieldError>}
    </div>
  );

  const gratuityOptionsEditor = (
    <div style={{ marginBottom: "16px" }}>
      <FieldLabel>{tr("Options")}</FieldLabel>
      <p className="font-inter" style={{ fontSize: "13px", lineHeight: "18px", color: TEXT_DIM, margin: "-2px 0 10px" }}>
        {tr("Shown on the till in this order; the default is preselected.")}
      </p>
      {p.gratuityForm.options.map((row, index) => {
        const rowError = gratuityTouched ? gratuityOptionValueProblem(row) : null;
        const rowLabel = `${tr("Option")} ${index + 1}`;
        const setOptions = (next: (options: GratuityFormState["options"]) => GratuityFormState["options"]) =>
          p.setGratuityForm((prev: GratuityFormState) => ({ ...prev, options: next(prev.options) }));
        return (
          <div
            key={row.key}
            role="group"
            aria-label={rowLabel}
            style={{ border: `1px solid ${BORDER_STRONG}`, borderRadius: RADIUS_INPUT, padding: "12px 12px 0", marginBottom: "12px" }}
          >
            <div style={{ marginBottom: "12px" }}>
              <SegmentedControl
                ariaLabel={`${rowLabel}: ${tr("Type")}`}
                wrap
                options={GRATUITY_OPTION_TYPES.map((type) => ({ value: String(type.id), label: tr(type.name) }))}
                value={String(row.valueType)}
                onChange={(v) => setOptions((options) => patchGratuityOption(options, row.key, { valueType: Number(v) }))}
              />
            </div>
            <Field
              label={row.valueType === 3 ? tr("Round up step") : tr("Value")}
              required
              name={`gratuity-value-${row.key}`}
              inputMode="decimal"
              placeholder={row.valueType === 1 ? "10" : row.valueType === 2 ? "2.50" : "1.00"}
              value={row.value}
              onChange={(e) => setOptions((options) => patchGratuityOption(options, row.key, { value: e.target.value }))}
              onBlur={() => setGratuityTouched(true)}
              error={rowError ? tr(rowError) : undefined}
            />
            <Field
              label={tr("Display name")}
              name={`gratuity-name-${row.key}`}
              placeholder={tr("Optional")}
              maxLength={100}
              value={row.displayName}
              onChange={(e) => setOptions((options) => patchGratuityOption(options, row.key, { displayName: e.target.value }))}
            />
            <div className="flex items-center" style={{ gap: "8px", paddingBottom: "12px", flexWrap: "wrap" }}>
              <button
                type="button"
                className="font-inter"
                aria-pressed={row.isDefault}
                onClick={() => setOptions((options) => setDefaultGratuityOption(options, row.key, !row.isDefault))}
                style={{ ...pillButton(row.isDefault), height: "36px", padding: "0 14px" }}
              >
                {row.isDefault ? `✓ ${tr("Default")}` : tr("Default")}
              </button>
              <span style={{ flex: 1 }} />
              <button
                type="button"
                className="font-inter"
                aria-label={`${tr("Move up")} · ${rowLabel}`}
                disabled={index === 0}
                onClick={() => setOptions((options) => moveGratuityOption(options, row.key, -1))}
                style={{ ...pillButton(false), height: "36px", padding: "0 12px", opacity: index === 0 ? 0.4 : 1 }}
              >
                ↑
              </button>
              <button
                type="button"
                className="font-inter"
                aria-label={`${tr("Move down")} · ${rowLabel}`}
                disabled={index === p.gratuityForm.options.length - 1}
                onClick={() => setOptions((options) => moveGratuityOption(options, row.key, 1))}
                style={{
                  ...pillButton(false),
                  height: "36px",
                  padding: "0 12px",
                  opacity: index === p.gratuityForm.options.length - 1 ? 0.4 : 1,
                }}
              >
                ↓
              </button>
              <button
                type="button"
                className="font-inter"
                aria-label={`${tr("Remove")} · ${rowLabel}`}
                onClick={() => setOptions((options) => removeGratuityOption(options, row.key))}
                style={{ ...pillButton(false, true), height: "36px", padding: "0 14px" }}
              >
                {tr("Remove")}
              </button>
            </div>
          </div>
        );
      })}
      {p.gratuityForm.options.length === 0 && (
        <p className="font-inter" style={{ padding: "8px 0 12px", fontSize: "14px", color: TEXT_DIM, textAlign: "center" }}>
          {tr("No options yet. Add at least one preset.")}
        </p>
      )}
      {gratuityListError && <FieldError>{tr(gratuityListError)}</FieldError>}
      <button
        type="button"
        className="font-inter"
        onClick={() => p.setGratuityForm((prev: GratuityFormState) => ({ ...prev, options: [...prev.options, newGratuityOptionRow()] }))}
        style={{ ...pillButton(false), width: "100%", marginTop: gratuityListError ? "10px" : 0 }}
      >
        <span style={{ fontSize: "18px", lineHeight: 1 }}>+</span>
        {tr("Add option")}
      </button>
    </div>
  );

  const gratuitySheet = (
    <BottomSheet
      open={gratuitySheetOpen}
      onClose={closeGratuitySheet}
      title={p.editingGratuityId ? tr("Edit gratuity settings") : tr("Add gratuity settings")}
      footer={
        <StickyFooter
          position="static"
          primaryLabel={tr("Save")}
          onPrimary={() => {
            setGratuityTouched(true);
            if (gratuityProblem) {
              scrollToFirstError(gratuitySheetRef.current);
              return;
            }
            p.onSaveGratuity();
          }}
          cancelLabel={tr("Cancel")}
          onCancel={closeGratuitySheet}
        />
      }
    >
      <div ref={gratuitySheetRef} style={{ padding: "0 20px" }}>
        {gratuitySelect(
          tr("Applies to"),
          p.gratuityForm.locationId,
          (v) => p.setGratuityForm((prev: GratuityFormState) => ({ ...prev, locationId: v })),
          [{ id: "", name: tr("Company default") }, ...p.gratuityLocations],
        )}
        {gratuityYesNo("enable", tr("Enable"))}
        {gratuityOptionsEditor}
        {gratuityYesNo("allowCustom", tr("Allow custom gratuity"))}
        {p.gratuityForm.allowCustom === "yes" && (
          <Field
            label={tr("Maximum custom %")}
            name="gratuity-custom-max"
            inputMode="decimal"
            placeholder={tr("No limit")}
            value={p.gratuityForm.customMaxPercent}
            {...limitProps(gratuityLimits, "customMaxPercent", "number")}
            onChange={(e) => p.setGratuityForm((prev: GratuityFormState) => ({ ...prev, customMaxPercent: e.target.value }))}
            onBlur={() => setGratuityTouched(true)}
            error={gratuityCustomMaxError ? tr(gratuityCustomMaxError) : p.gratuityErrors?.customMaxPercent}
          />
        )}
        {gratuityYesNo("cashChange", tr("Offer cash change as gratuity"))}
        {gratuityYesNo("roundUpExact", tr("Round up to the next step when already exact"))}
        {gratuitySelect(
          tr("Prompt mode"),
          String(p.gratuityForm.promptMode),
          (v) => p.setGratuityForm((prev: GratuityFormState) => ({ ...prev, promptMode: Number(v) })),
          GRATUITY_PROMPT_MODES.map((mode) => ({ id: String(mode.id), name: tr(mode.name) })),
        )}
        {gratuitySelect(
          tr("Percent base"),
          String(p.gratuityForm.percentBase),
          (v) => p.setGratuityForm((prev: GratuityFormState) => ({ ...prev, percentBase: Number(v) })),
          GRATUITY_PERCENT_BASES.map((base) => ({ id: String(base.id), name: tr(base.name) })),
        )}
        {gratuityYesNo("taxable", tr("Taxable"))}
        {p.gratuityForm.taxable === "yes" &&
          gratuitySelect(
            tr("Tax group"),
            p.gratuityForm.taxGroupId,
            (v) => p.setGratuityForm((prev: GratuityFormState) => ({ ...prev, taxGroupId: v })),
            p.gratuityTaxGroups,
            tr("Select tax group"),
            gratuityTaxGroupError,
          )}
      </div>
    </BottomSheet>
  );

  /* ---------------- Footer per tab ---------------- */

  let footer: React.ReactNode = null;
  if (tab === "company") {
    footer = (
      <StickyFooter
        primaryLabel={t(language, "Save")}
        onPrimary={submitCompany}
        cancelLabel={t(language, "Cancel")}
        onCancel={cancelCompany}
      />
    );
  } else if (tab === "invoice") {
    footer = (
      <StickyFooter
        primaryLabel={t(language, "Save")}
        onPrimary={submitInvoiceConfig}
        cancelLabel={t(language, "Cancel")}
        onCancel={cancelCompany}
      />
    );
  } else if (tab === "user" && p.permissions.users) {
    footer = <StickyFooter primaryLabel={t(language, "Add User")} onPrimary={() => webNavigate("/" + preLanguage + "/companies/user/new")} />;
  } else if (tab === "location") {
    footer = <StickyFooter primaryLabel={t(language, "Add Location")} onPrimary={() => webNavigate("/" + preLanguage + "/companies/location/new")} />;
  } else if (tab === "serviceCharge") {
    // At most one per company: once it exists the footer edits it instead of
    // offering another one.
    const existingCharge = p.listServiceCharge[0];
    footer = existingCharge ? (
      <StickyFooter
        primaryLabel={t(language, "Edit service charge")}
        onPrimary={async () => {
          // Open only once the record has loaded, as in the record sheet.
          const loaded = await p.onEditServiceCharge(existingCharge.id);
          if (loaded) openChargeSheet();
        }}
      />
    ) : (
      <StickyFooter
        primaryLabel={t(language, "Add service charge")}
        onPrimary={() => {
          p.onAddServiceCharge();
          openChargeSheet();
        }}
      />
    );
  } else if (tab === "gratuity") {
    footer = (
      <StickyFooter
        primaryLabel={t(language, "Add gratuity settings")}
        onPrimary={() => {
          p.onAddGratuity();
          openGratuitySheet();
        }}
      />
    );
  }

  const deleteItemName = deleteItem?.item?.Name ?? "";

  return (
    <div className="flex flex-col h-full min-h-0" style={{ position: "relative" }}>
      {/* The page's single scroll surface. */}
      <div
        ref={pageRef}
        className="scroll-container"
        style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch" }}
      >
        <div style={{ position: "sticky", top: 0, zIndex: 5, backgroundColor: "#FFFFFF", paddingBottom: "8px" }}>
          <SegmentedControl ariaLabel={t(language, "Company sections")} options={tabs} value={tab} onChange={p.onChangeTab} />
        </div>
        <div style={{ paddingBottom: footer ? `${STICKY_FOOTER_HEIGHT + 16}px` : "16px" }}>
          {tab === "company" && companyTab}
          {tab === "billing" && billingTab}
          {tab === "user" && entityList("user", p.listUser, p.filterUsers, p.onPageUsers, setRecordUser)}
          {tab === "location" && entityList("location", p.listLocation, p.filterLocations, p.onPageLocations, setRecordLocation)}
          {tab === "invoice" && invoiceTab}
          {tab === "serviceCharge" && serviceChargeTab}
          {tab === "gratuity" && gratuityTab}
        </div>
      </div>

      {footer}

      {countrySheet}
      {inviteSheet}
      {bankSheet}
      {bankRecordSheet}
      {chargeSheet}
      {chargeRecordSheet}
      {gratuitySheet}
      {gratuityRecordSheet}
      {recordSheet("user", recordUser, () => setRecordUser(null))}
      {recordSheet("location", recordLocation, () => setRecordLocation(null))}

      <ConfirmSheet
        open={!!removeAccountant}
        title={t(language, "Remove accountant?")}
        message={
          <>
            {t(language, "You will remove access for")}{" "}
            <strong style={{ color: TEXT }}>
              {removeAccountant?.user?.firstName} {removeAccountant?.user?.lastName}
            </strong>
            {removeAccountant?.user?.emailAddress ? ` (${removeAccountant.user.emailAddress})` : ""}.
          </>
        }
        confirmLabel={t(language, "Remove")}
        cancelLabel={t(language, "Cancel")}
        onCancel={() => setRemoveAccountant(null)}
        onConfirm={() => {
          const v = removeAccountant;
          setRemoveAccountant(null);
          p.onRemoveAccountant(v);
        }}
      />

      <ConfirmSheet
        open={p.cancelSubscriptionOpen}
        title={t(language, "Cancel Subscription")}
        message={t(
          language,
          "Your plan will be canceled, but is still available until the end of your current billing period on 31/01/2026. If you change your mind, you can renew your subscription.",
        )}
        confirmLabel={t(language, "Cancel subscription")}
        cancelLabel={t(language, "Keep subscription")}
        onCancel={() => p.setCancelSubscriptionOpen(false)}
        onConfirm={() => p.setCancelSubscriptionOpen(false)}
      />

      <ConfirmSheet
        open={!!deleteItem}
        title={deleteItem?.tab === "user" ? t(language, "Delete user?") : t(language, "Delete location?")}
        message={
          <>
            {t(language, "You will permanently delete")} <strong style={{ color: TEXT }}>{deleteItemName}</strong>
            {deleteItem?.item?.Code ? ` (${deleteItem.item.Code})` : ""}.
          </>
        }
        confirmLabel={t(language, "Delete")}
        cancelLabel={t(language, "Cancel")}
        onCancel={() => setDeleteItem(null)}
        onConfirm={() => {
          const target = deleteItem;
          setDeleteItem(null);
          if (target) p.onDeleteItem(target.tab, target.item.id);
        }}
      />

      <ConfirmSheet
        open={!!deleteBank}
        title={t(language, "Delete bank detail?")}
        message={
          <>
            {t(language, "You will permanently delete")} <strong style={{ color: TEXT }}>{deleteBank?.bankName}</strong>
            {deleteBank && bankLine(deleteBank) ? ` (${bankLine(deleteBank)})` : ""}.
          </>
        }
        confirmLabel={t(language, "Delete")}
        cancelLabel={t(language, "Cancel")}
        onCancel={() => setDeleteBank(null)}
        onConfirm={() => {
          const bank = deleteBank;
          setDeleteBank(null);
          if (bank) p.onDeleteBankDetail(bank.id);
        }}
      />

      <BottomSheet
        open={p.noticeOpen}
        onClose={p.onCloseNotice}
        title={t(language, "Company")}
        footer={<StickyFooter position="static" primaryLabel={t(language, "OK")} onPrimary={p.onCloseNotice} />}
      >
        <p className="font-inter" style={{ padding: "0 20px 16px", fontSize: "15px", lineHeight: "22px", color: TEXT_MUTED }}>
          {p.noticeMessage}
        </p>
      </BottomSheet>

      <ConfirmSheet
        open={p.deleteServiceChargeOpen}
        title={t(language, "Delete Service charge?")}
        message={
          deleteChargeItem ? (
            <>
              {t(language, "You will permanently delete")}{" "}
              <strong style={{ color: TEXT }}>
                {t(language, deleteChargeItem.chargeTypeText)} · {chargeValueLabel(deleteChargeItem)}
              </strong>
              .
            </>
          ) : (
            t(language, "You will permanently delete the service charge")
          )
        }
        confirmLabel={t(language, "Delete")}
        cancelLabel={t(language, "Cancel")}
        onCancel={p.onCancelDeleteServiceCharge}
        onConfirm={p.onConfirmDeleteServiceCharge}
      />

      <ConfirmSheet
        open={p.deleteGratuityOpen}
        title={t(language, "Delete gratuity settings?")}
        message={
          deleteGratuityItem ? (
            <>
              {t(language, "You will permanently delete")}{" "}
              <strong style={{ color: TEXT }}>{p.gratuityScopeName(deleteGratuityItem)}</strong>.
            </>
          ) : (
            t(language, "You will permanently delete the gratuity settings")
          )
        }
        confirmLabel={t(language, "Delete")}
        cancelLabel={t(language, "Cancel")}
        onCancel={p.onCancelDeleteGratuity}
        onConfirm={p.onConfirmDeleteGratuity}
      />
    </div>
  );
};

export default CompanyMobile;
